import { useState, type FormEvent, type ReactNode } from 'react';
import { LoadingState } from '../components/states.js';
import { BrandMark } from '../components/brand.js';
import { Button, Card, Field, Notice } from '../components/ui.js';
import { useSession } from './session.js';

/*
 * Prihlasovacia brana.
 *
 * Jedina obrazovka, ktoru clovek uvidi, kym nema pristup - preto je
 * uplne holá az na to, co treba: znacka, jeden formular, jedna hlaska.
 * Ziadna navigacia, ziadne odkazy inam.
 */

interface Udaje {
  readonly token: string;
  readonly username: string;
  readonly password: string;
}

function Formular({
  title,
  detail,
  submitLabel,
  onSubmit,
  tokenField,
}: {
  readonly title: string;
  readonly detail: string;
  readonly submitLabel: string;
  readonly onSubmit: (udaje: Udaje) => Promise<void>;
  readonly tokenField?: { readonly label: string; readonly hint: string };
}): ReactNode {
  const [token, setToken] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [chyba, setChyba] = useState<string | undefined>(undefined);
  const [odosiela, setOdosiela] = useState(false);

  const odosli = (event: FormEvent) => {
    event.preventDefault();
    setChyba(undefined);
    setOdosiela(true);
    void onSubmit({ token, username, password })
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Prihlásenie zlyhalo.');
      })
      .finally(() => {
        setOdosiela(false);
      });
  };

  return (
    <div className="flex min-h-full items-center justify-center bg-(--color-surface) px-4 py-10">
      <div className="flex w-full max-w-sm flex-col gap-6">
        <div className="flex flex-col items-center gap-3 text-center">
          {/*
           * Nazov patri k znacke aj tu. Samotny symbol nepovie, na com
           * clovek je - a prihlasovacia obrazovka byva prve, co vidi.
           */}
          <div className="flex items-center gap-2.5">
            <span className="text-(--color-accent)">
              <BrandMark className="size-10" />
            </span>
            <span className="text-2xl font-semibold tracking-tight text-(--color-text)">
              LoxLink
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <h1 className="text-xl font-semibold tracking-tight text-(--color-text)">{title}</h1>
            <p className="text-sm text-(--color-text-muted)">{detail}</p>
          </div>
        </div>

        <Card className="p-5">
          <form onSubmit={odosli} className="flex flex-col gap-4">
            {tokenField && (
              <Field
                label={tokenField.label}
                value={token}
                onChange={setToken}
                hint={tokenField.hint}
                required
                autoComplete="off"
              />
            )}

            <Field
              label="Prihlasovacie meno"
              value={username}
              onChange={setUsername}
              required
              autoComplete="username"
            />

            <Field
              label="Heslo"
              type="password"
              value={password}
              onChange={setPassword}
              required
              autoComplete={tokenField ? 'new-password' : 'current-password'}
              {...(tokenField
                ? { hint: 'Aspoň 12 znakov. Dlhšie heslo je lepšie než zložitejšie.' }
                : {})}
            />

            {chyba !== undefined && <Notice tone="danger">{chyba}</Notice>}

            <Button type="submit" variant="primary" disabled={odosiela}>
              {odosiela ? 'Odosielam…' : submitLabel}
            </Button>
          </form>
        </Card>

        <p className="text-center text-xs text-(--color-text-subtle)">
          LoxLink je nezávislý open-source projekt. Nie je spojený so spoločnosťou Loxone ani s
          1Home.
        </p>
      </div>
    </div>
  );
}

/** Pustí ďalej len prihláseného používateľa. */
export function AuthGate({ children }: { readonly children: ReactNode }): ReactNode {
  const relacia = useSession();

  if (relacia.stav === 'nacitava') {
    return (
      <div className="flex min-h-full items-center justify-center p-6">
        <LoadingState what="Overujem prihlásenie" />
      </div>
    );
  }

  if (relacia.stav === 'prvotne-nastavenie') {
    return (
      <Formular
        title="Prvotné nastavenie"
        detail="Zatiaľ neexistuje žiadny účet. Prvého správcu založíš jednorazovým tokenom z logu servera."
        submitLabel="Založiť účet"
        tokenField={{
          label: 'Setup token',
          hint: 'Nájdeš ho vo výpise servera pri štarte. Platí do reštartu brány.',
        }}
        onSubmit={({ token, username, password }) => relacia.setup(token, username, password)}
      />
    );
  }

  if (relacia.stav === 'odhlaseny') {
    return (
      <Formular
        title="Prihlásenie"
        detail="Zadaj prihlasovacie údaje k tejto bráne."
        submitLabel="Prihlásiť sa"
        onSubmit={({ username, password }) => relacia.login(username, password)}
      />
    );
  }

  return children;
}
